/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-use-before-define*/
export class LazyAsyncIter<T> implements AsyncIterator<T>, AsyncIterable<T> {
  protected _asyncIter: AsyncIterator<T>;

  constructor(asyncIter: AsyncIterator<T>) {
    this._asyncIter = asyncIter;
  }

  public async next(): Promise<IteratorResult<any>> {
    return await this._asyncIter.next();
  }

  public filter(predicate: (val: T) => boolean): LazyAsyncFilter<T> {
    return new LazyAsyncFilter(this, predicate);
  }

  public map<R>(mapFn: (val: T) => R): LazyAsyncMap<T, R> {
    return new LazyAsyncMap<T, R>(this, mapFn);
  }

  public mapAwait<R>(mapFn: (val: T) => Promise<R>): LazyAsyncMapAwait<T, R> {
    return new LazyAsyncMapAwait<T, R>(this, mapFn);
  }

  public forEach<T>(forEachFn: (val: T) => void): LazyAsyncForEach<T> {
    return new LazyAsyncForEach<T>(this, forEachFn);
  }

  public async collect(): Promise<T[]> {
    const buffer: T[] = [];
    for await (const element of this) {
      buffer.push(element);
    }
    return buffer;
  }
  public [Symbol.asyncIterator](): AsyncIterator<T> {
    return {
      next: async (): Promise<any> => {
        return await this.next();
      },
    };
  }
}

//TODO: Fix type casting. I'm lying to the compiler here.
export class LazyAsyncMap<T, R> extends LazyAsyncIter<R> {
  private _mapFn: (val: T) => R;
  constructor(asyncIter: AsyncIterator<T>, mapFn: (val: T) => R) {
    super(asyncIter as AsyncIterator<R>);
    this._mapFn = mapFn;
  }
  public async next(): Promise<IteratorResult<R>> {
    const item: IteratorResult<T> =
      (await this._asyncIter.next()) as IteratorResult<T>;
    if (!item.done) {
      return { value: this._mapFn(item.value), done: item.done };
    } else {
      return { value: undefined, done: true };
    }
  }
}

export class LazyAsyncMapAwait<T, R> extends LazyAsyncIter<R> {
  private _mapFn: (val: T) => Promise<R>;
  constructor(asyncIter: AsyncIterator<T>, mapFn: (val: T) => Promise<R>) {
    super(asyncIter as AsyncIterator<R>);
    this._mapFn = mapFn;
  }
  public async next(): Promise<IteratorResult<R>> {
    const item: IteratorResult<T> =
      (await this._asyncIter.next()) as IteratorResult<T>;
    if (!item.done) {
      return { value: await this._mapFn(item.value), done: item.done };
    } else {
      return { value: undefined, done: true };
    }
  }
}

export class LazyAsyncForEach<T> extends LazyAsyncIter<T> {
  private _forEachFn: (val: T) => void;
  constructor(asyncIter: AsyncIterator<T>, forEachFn: (val: T) => void) {
    super(asyncIter);
    this._forEachFn = forEachFn;
  }
  public async next(): Promise<IteratorResult<T>> {
    const item = await this._asyncIter.next();
    this._forEachFn(item.value);
    if (!item.done) {
      return { value: item.value, done: item.done };
    } else {
      return { value: item.value, done: true };
    }
  }
}
export class LazyAsyncFilter<T> extends LazyAsyncIter<T> {
  private _predicate: (val: T) => boolean;
  private _counterMax = 1_000_000;
  private _counter = 0;
  constructor(asyncIter: AsyncIterator<T>, predicate: (val: T) => boolean) {
    super(asyncIter);
    this._predicate = predicate;
  }
  public async next(): Promise<IteratorResult<T>> {
    while (++this._counter < this._counterMax) {
      const item = await this._asyncIter.next();
      if (item.done) {
        return { value: undefined, done: item.done };
      }
      if (this._predicate(item.value)) {
        return { value: item.value, done: item.done };
      }
    }
    throw Error("Max Iterarion on Filter");
  }
}
